import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Sparkles, Plus, Home, Archive, LogOut, ChevronRight, Shield } from "lucide-react";

export function Sidebar({ mobileOpen, onClose }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const loc = useLocation();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [classes, setClasses] = useState([]);

  const load = async () => { try { setClasses(await api.listClasses()); } catch { setClasses([]); } };
  useEffect(() => { load(); }, [loc.pathname]);

  const active = classes.filter((c) => !c.archived);
  const archived = classes.filter((c) => c.archived);
  const go = (path) => { navigate(path); onClose?.(); };

  const content = (
    <div className="flex flex-col h-full">
      <button data-testid="sidebar-logo" onClick={() => go("/")} className="flex items-center gap-2 px-5 h-16 border-b border-border shrink-0">
        <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center overflow-hidden"><img src="https://customer-assets-4nw71qhi.emergentagent.net/job_dojo-gamified/artifacts/q9a4jl7m_Cyberon-LogoW-T.png" alt="Cyberon" className="w-7 h-7 object-contain" /></div>
        <span className="font-display font-extrabold text-lg tracking-tight">Cyberon <span className="text-primary">Dojo</span></span>
      </button>

      <div className="flex-1 overflow-auto scrollbar-thin px-3 py-4 space-y-1">
        <button data-testid="sidebar-home" onClick={() => go("/")} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted font-semibold text-sm transition-colors"><Home className="w-4 h-4" /> {t("home")}</button>
        {user?.role === "admin" && (
          <button data-testid="sidebar-admin" onClick={() => go("/admin")} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl font-semibold text-sm transition-colors ${loc.pathname === "/admin" ? "bg-accent text-accent-foreground" : "hover:bg-muted"}`}><Shield className="w-4 h-4" /> {t("admin")}</button>
        )}

        <div className="pt-4 pb-1 px-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("myClasses")}</div>
        {active.map((c) => (
          <button key={c.id} data-testid={`sidebar-class-${c.id}`} onClick={() => go(`/clase/${c.id}`)}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${id === c.id ? "bg-accent text-accent-foreground" : "hover:bg-muted"}`}>
            <span className="w-6 h-6 rounded-lg flex items-center justify-center text-sm shrink-0" style={{ background: c.color + "22" }}>{c.emoji || "🎓"}</span>
            <span className="truncate flex-1 text-left">{c.name}</span>
            {id === c.id && <ChevronRight className="w-4 h-4" />}
          </button>
        ))}
        {active.length === 0 && <p className="px-3 text-xs text-muted-foreground">—</p>}
        <button data-testid="sidebar-new-class" onClick={() => go("/?new=1")} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-primary hover:bg-accent font-bold text-sm transition-colors mt-1"><Plus className="w-4 h-4" /> {t("newClass")}</button>

        {archived.length > 0 && (
          <>
            <div className="pt-4 pb-1 px-3 text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Archive className="w-3.5 h-3.5" /> {t("archived")}</div>
            {archived.map((c) => (<button key={c.id} onClick={() => go(`/clase/${c.id}`)} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm hover:bg-muted opacity-60"><span>{c.emoji || "🎓"}</span><span className="truncate">{c.name}</span></button>))}
          </>
        )}
      </div>

      <div className="border-t border-border p-3 space-y-2 shrink-0">
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-xl bg-muted">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">{user?.name?.charAt(0).toUpperCase() || "?"}</div>
          <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{user?.name}</div><div className="text-xs text-muted-foreground">{user?.role === "admin" ? t("admin") : t("teacher")}</div></div>
          <button data-testid="logout-btn" onClick={logout} title={t("logout")} className="w-8 h-8 rounded-lg hover:bg-destructive hover:text-white flex items-center justify-center"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden md:block w-64 shrink-0 bg-card border-r border-border sticky top-0 h-screen">{content}</aside>
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-card border-r border-border h-full">{content}</div>
          <div className="flex-1 bg-black/40" onClick={onClose} />
        </div>
      )}
    </>
  );
}
